/**
 * Display formatting the SERVER also has to do.
 *
 * WHY THIS IS IN THE CONTRACTS PACKAGE AT ALL. For the reason
 * `PLANOGRAM_NO_RANK_LABEL` gives for itself: the console cell, the XLSX cell and
 * the PDF cell must read identically, and a reader comparing a workbook against
 * the screen it came from is exactly who that protects. What makes a duration
 * different from those labels is that one of the printers is the api —
 * `export.service.ts` renders the PDF — and the api does not depend on
 * `@fieldvision/core`, whose header calls it "behaviour shared by every
 * FieldVision CLIENT" and whose peer dependencies are react, zustand and
 * react-query. This is the only package all three deployment units already have.
 *
 * Nothing here is emitted as JSON Schema; ai-svc prints nothing.
 */
/**
 * Seconds → "<1 mnt", "45 mnt", "1 jam 23 mnt". A visit's length, everywhere.
 *
 * THE UNITS ARE SPELLED OUT BECAUSE THE SHORT ONES WERE READ WRONG. The five
 * copies this replaces printed "1j 23m" and, below a minute, `${total}d` for
 * detik. A visit whose check-in and check-out were one second apart therefore
 * rendered as "1d" — and "d" is seconds in Indonesian and days in every
 * dashboard anyone has ever used. There is no winning that with one letter.
 *
 * SUB-MINUTE IS "<1 mnt", NOT "0 mnt" AND NOT THE SECONDS. The exact second
 * count of a three-second visit answers no question this column is asked; what
 * the reader needs is that it happened and was implausibly brief. "0 mnt" beside
 * a real check-out time reads as missing data instead, which buries the finding.
 * The old copies disagreed with each other about this: the console printed "1d"
 * and `export.service.ts` printed "0m" for the SAME visit.
 *
 * WHOLE MINUTES, FLOORED. 90 seconds is "1 mnt". A visit is as long as the
 * minutes that have actually elapsed; rounding up would report two minutes of
 * work for a visit that was ninety-one seconds.
 *
 * THE MINUTES ARE ALWAYS PRINTED, so an even hour is "1 jam 0 mnt". These land in
 * right-aligned `tabular-nums` columns and in a fixed-width PDF cell, and a
 * ragged "1 jam" above "1 jam 23 mnt" defeats the alignment those columns exist
 * for — the same trade `multiplier` makes for its fixed decimals.
 *
 * TAKES A STRING AS WELL AS A NUMBER. `avg()` comes back from the driver as a
 * numeric string, which is why `wire.ts` types `averageDurationSeconds` as
 * `string | null`; `Number('')` is 0, so empty has to be absent here rather than
 * a confident zero. A NEGATIVE is not absent — it is a check-out recorded before
 * its check-in, by a clock that moved — so it falls in the sub-minute branch
 * rather than the em-dash, which would claim nothing was recorded at all.
 */
export function formatDuration(seconds) {
    if (seconds === null || seconds === undefined || seconds === '')
        return '—';
    const total = Number(seconds);
    // Checked before the floor rather than after, so no reader has to work out
    // whether `Math.floor(NaN)` was being relied on.
    if (!Number.isFinite(total))
        return '—';
    if (total < 60)
        return '<1 mnt';
    const minutes = Math.floor(total / 60);
    if (minutes < 60)
        return `${minutes} mnt`;
    return `${Math.floor(minutes / 60)} jam ${minutes % 60} mnt`;
}
/**
 * A region in a picker, a report row and a workbook cell — `900024 - BANDUNG`.
 *
 * THE CODE LEADS, because the code is the identity. A region is a
 * sub-distributor; `subdist_code` is its unique key. Two sub-distributors
 * routinely work areas with the same name, so an option reading "BEKASI" alone
 * is a coin toss, and the wrong pick moves an outlet into someone else's scope.
 *
 * THE CODE, NOT THE SUB-DISTRIBUTOR'S NAME: `PT. PERKASA GEMILANG DISTRINDO
 * BANDUNG` is thirty-seven characters identical across everything that
 * sub-distributor touches, so it pushes the distinguishing part off the end of a
 * narrow select. `900024` is what the distributor's own files quote anyway. The
 * full name is on the region master screen, where there is room for a column.
 *
 * ORDERING IS LOAD-BEARING. A native `<select>`'s keyboard type-ahead matches
 * the LABEL PREFIX, so every list this feeds must be sorted by `subdistCode` or
 * typing `900` lands somewhere arbitrary — see `useRegions` and
 * `listVisitableAreas`. And a spaced hyphen rather than the `·` it replaced: it
 * is typeable, it survives a paste into a spreadsheet cell and find-in-page, and
 * it stays clear of the retired derived code, which slugged the area and had no
 * spaces.
 *
 * THE SERVER COMPOSES THIS IN SQL rather than calling this function, because
 * reports group and sort on the label — see `regionLabelExpr` in
 * `apps/api/src/shared/db`. The two are a pair and must agree; a contracts test
 * pins the format so a change here fails loudly rather than drifting.
 */
export function regionLabel(region) {
    return `${region.subdistCode} - ${region.area}`;
}
//# sourceMappingURL=format.js.map